'use client';

import { useInsertionEffect, useRef } from 'react';
import type { TrackProps } from '@/contexts/mixpanel/client';

/** 宿主与预览协议拥有的字段；调用方传入的同名值不透传，避免伪造。 */
export const RESERVED_EVENT_PROPERTIES = ['app_version', 'deployment_environment', 'host_preview_protocol_version'] as const;

/** 事件发生时同步调用，返回该事件的业务属性默认值（如套餐）。 */
export type EventPropertiesSource = (eventName: string) => TrackProps | undefined;

const sources = new Set<{ current: EventPropertiesSource }>();

/**
 * 生成事件发生时的业务快照，Mixpanel 与 CGP 双写共用同一份：
 * 已挂载来源的默认值 < 调用方属性；保留字段只取来源给出的值。
 */
export function captureEventProperties(eventName: string, props?: TrackProps): TrackProps | undefined {
    let captured: TrackProps | undefined;
    for (const source of sources) {
        try {
            const value = source.current(eventName);
            if (value) captured = { ...captured, ...value };
        } catch {
            console.error('[analytics] event properties source failed');
        }
    }
    if (props) {
        const own = { ...props };
        for (const key of RESERVED_EVENT_PROPERTIES) delete own[key];
        captured = { ...captured, ...own };
    }
    return captured;
}

/**
 * App 在拥有业务数据的布局里登记事件属性；卸载即失效，离开该区域后不会串到其它页面。
 * 来源函数始终读最新值，属性变化不重新登记、也不重挂 SDK Provider。
 */
export function useMixpanelEventProperties(source: EventPropertiesSource): void {
    const latest = useRef(source);
    // insertion 阶段早于子树的 layout/passive effect，页面挂载时上报的事件也能取到属性。
    useInsertionEffect(() => {
        latest.current = source;
    });
    useInsertionEffect(() => {
        const entry = latest;
        sources.add(entry);
        return () => {
            sources.delete(entry);
        };
    }, []);
}
